import React, { JSX, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  CHAT_MESSAGE_WEIGHT_DEFAULT,
  CHAT_USERNAME_WEIGHT_DEFAULT,
  chatFontFamily,
  effectiveChatFontWeight,
  preRangePinnedWeight
} from 'lib/chatFonts'
import { useFontGate } from 'lib/fontLoader'
import { useSearchParams } from 'react-router'
import { graphqlRequest } from 'lib/helpers'
import { connectTwitchChat } from 'lib/twitchChat'
import {
  ChatBadge,
  ChatMessage,
  YouTubeRoles,
  fallbackColor,
  messageSegments
} from 'lib/twitchChatParse'
import {
  EmoteEntry,
  EmoteLookup,
  EmoteStore,
  OverlaySegment,
  ThirdPartyEmoteSegment,
  applyThirdPartyEmotes,
  createEmoteStore,
  nativeEmoteUrlLarge
} from 'lib/thirdPartyEmotes'
import { buildAppSyncWsClient, WEBSOCKET_SUB_QUERY } from 'lib/appSyncSubscription'
import { OverlayAutoFit, OverlayFitMode, useOverlayFit } from 'components/OverlayAutoFit'
import { PLATFORM_MARKS, PLATFORM_MARK_STYLE } from 'components/chatOverlays/platformMarks'
import { YouTubeRoleBadges } from 'components/chatOverlays/youtubeRoleBadges'
import { OverlayPreviewSeam, previewSeamActive } from 'components/overlayPreview/previewSeam'
import { ChatOverlayPreviewConfig } from 'components/overlayPreview/previewConfig'
import {
  ChatPreviewFeed,
  sampleTagStyle,
  useChatFeedSnapshot,
  useDocumentVisible,
  usePrefersReducedMotion,
  useSampleChatWindow
} from 'components/overlayPreview/previewFeeds'
import {
  SAMPLE_BADGE_ART,
  SAMPLE_OPACITY,
  SampleChatContext,
  elementSeed,
  sampleChatMessage
} from 'lib/overlaySamples'
import getChatOverlayDataQuery from 'graphql/getChatOverlayDataById.graphql'
import logger from 'logger'

const log = logger.extend('chat-overlay')

const MAX_MESSAGES = 100
const MAX_PENDING_MESSAGES = 2000
const FLUSH_INTERVAL_MS = 50
const MAX_DELAY_SECONDS = 60
const REPLAY_CAP_MS = MAX_DELAY_SECONDS * 1000
const PACE_GAP_CAP_MS = 2000
const BOOTSTRAP_RETRY_MS = 15000
const EMPTY_CHANNELS_RETRY_MS = 60000

interface ApiBadgeVersion {
  version: string
  url: string
}

interface ApiBadgeSet {
  set: string
  versions: ApiBadgeVersion[]
}

interface ApiChannelBadges {
  broadcasterUserId: string
  sets: ApiBadgeSet[]
}

interface ApiChatChannel {
  channelLogin: string
  broadcasterUserId: string
  profileImageUrl: string | null
}

interface ChatOverlayConfig {
  chatId: string
  name: string
  showTextShadow: boolean
  textShadow: string
  showBadges: boolean
  showChannelPills: boolean
  usernameFontSize: number
  usernameLineHeight: number
  messageFontSize: number
  messageLineHeight: number
  messageFontWeight: number
  usernameFontWeight: number
  imageScale: number
  messageColor: string
  blockedWords: string[]
  fontFamily: string
  youtubeSourceEnabled: boolean
  youtubeChatChannels?: string[] | null
  chatDelayMs: number
  width?: number | null
  height?: number | null
  show7tvEmotes?: boolean | null
  showBttvEmotes?: boolean | null
  showFfzEmotes?: boolean | null
  twitchChatChannels?: string[] | null
  showYouTubeProfilePictures?: boolean | null
}

interface ChatOverlayData {
  channels: ApiChatChannel[]
  globalBadges: ApiBadgeSet[]
  channelBadges: ApiChannelBadges[]
  config: ChatOverlayConfig
  accountChatChannel: string | null
}

interface PendingMessage {
  message: ChatMessage
  paceUntil: number
}

type BadgeMap = Record<string, Record<string, string>>

interface OverlayBootstrap {
  channels: string[]
  broadcasterUserIds: string[]
  globalBadges: BadgeMap
  channelBadges: Record<string, BadgeMap>
  avatars: Record<string, string | null>
  config: ChatOverlayConfig
  accountChatChannel: string | null
}

const chatDelayMsOf = (config: ChatOverlayConfig): number => {
  const raw = config.chatDelayMs
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return 0
  return Math.min(Math.max(0, Math.round(raw)), MAX_DELAY_SECONDS * 1000)
}

const MIN_STORED_DIMENSION = 100
const MAX_STORED_DIMENSION = 4000

const clampStoredDimension = (n: number): number =>
  Math.min(MAX_STORED_DIMENSION, Math.max(MIN_STORED_DIMENSION, Math.round(n)))

const storedDimensionsOf = (
  config: ChatOverlayConfig
): { width: number, height: number } | null => {
  const w = config.width
  const h = config.height
  if (typeof w !== 'number' || !Number.isFinite(w)) return null
  if (typeof h !== 'number' || !Number.isFinite(h)) return null
  return { width: clampStoredDimension(w), height: clampStoredDimension(h) }
}

const foldBadgeSets = (sets: ApiBadgeSet[]): BadgeMap =>
  sets.reduce<BadgeMap>((acc, s) => ({
    ...acc,
    [s.set]: s.versions.reduce<Record<string, string>>(
      (vs, v) => ({ ...vs, [v.version]: v.url }), {})
  }), {})

const foldBootstrap = (data: ChatOverlayData): OverlayBootstrap => ({
  channels: data.channels.map((c) => c.channelLogin),
  broadcasterUserIds: data.channels.map((c) => c.broadcasterUserId).filter((id) => id !== ''),
  globalBadges: foldBadgeSets(data.globalBadges),
  channelBadges: data.channelBadges.reduce<Record<string, BadgeMap>>((acc, c) => ({
    ...acc,
    [c.broadcasterUserId]: foldBadgeSets(c.sets)
  }), {}),
  avatars: data.channels.reduce<Record<string, string | null>>((acc, c) => ({
    ...acc,
    [c.channelLogin.toLowerCase()]: c.profileImageUrl
  }), {}),
  config: data.config,
  accountChatChannel: data.accountChatChannel ?? null
})

interface AccountChatEnvelope {
  kind?: string
  messages?: unknown[]
  authorChannelId?: string
  channelId?: string
  channelTitle?: string
  channelProfileImageUrl?: string
  chatId?: string
  version?: number
  deleted?: boolean
  data?: ChatOverlayData | null
}

const httpsUrlOrUndefined = (raw: unknown): string | undefined =>
  typeof raw === 'string' && raw.startsWith('https://') ? raw : undefined

const youtubeRolesOf = (raw: unknown): YouTubeRoles => {
  const r = (raw ?? {}) as Record<string, unknown>
  return {
    owner: r.owner === true,
    moderator: r.moderator === true,
    member: r.sponsor === true || r.member === true,
    verified: r.verified === true
  }
}

const youtubeChatMessage = (raw: unknown, sourceChannelId: string | undefined): ChatMessage | null => {
  const m = raw as {
    id?: unknown
    authorName?: unknown
    authorChannelId?: unknown
    authorProfileImageUrl?: unknown
    text?: unknown
    publishedAt?: unknown
    roles?: unknown
  }
  if (typeof m?.id !== 'string' || m.id === '' || typeof m.text !== 'string') return null
  const authorName = typeof m.authorName === 'string' ? m.authorName : ''
  const receivedAt = Date.now()
  const sent = typeof m.publishedAt === 'number' ? m.publishedAt : 0
  const authorAvatarUrl = httpsUrlOrUndefined(m.authorProfileImageUrl)
  return {
    id: m.id,
    platform: 'YOUTUBE',
    channel: '',
    roomId: '',
    login: typeof m.authorChannelId === 'string' ? m.authorChannelId : '',
    displayName: authorName,
    color: fallbackColor(authorName),
    badges: [],
    text: m.text,
    emotes: [],
    isAction: false,
    receivedAt,
    publishedAt: Number.isFinite(sent) && sent > 0 ? sent : receivedAt,
    ...(sourceChannelId !== undefined && { sourceChannelId }),
    ...(authorAvatarUrl !== undefined && { authorAvatarUrl }),
    youtubeRoles: youtubeRolesOf(m.roles)
  }
}

interface YouTubeSourceMeta {
  title: string | null
  imageUrl: string | null
}

interface ChannelPillData {
  label: string
  imageUrl: string | null
  onImageError?: () => void
}

const youtubeSourceCountOf = (config: ChatOverlayConfig): number => {
  if (Array.isArray(config.youtubeChatChannels)) return config.youtubeChatChannels.length
  return config.youtubeSourceEnabled ? 1 : 0
}

const shouldRetryEmptyChannels = (folded: OverlayBootstrap): boolean => {
  const configured = folded.config.twitchChatChannels
  if (Array.isArray(configured)) return configured.length > 0 && folded.channels.length === 0
  return folded.channels.length === 0
}

const previewBootstrapOf = (cfg: ChatOverlayPreviewConfig): OverlayBootstrap => ({
  channels: cfg.twitchChatChannels.map((login) => login.toLowerCase()),
  broadcasterUserIds: [],
  globalBadges: {},
  channelBadges: {},
  avatars: {},
  config: {
    chatId: '',
    name: '',
    showTextShadow: cfg.showTextShadow,
    textShadow: cfg.textShadow,
    showBadges: cfg.showBadges,
    showChannelPills: cfg.showChannelPills,
    usernameFontSize: cfg.usernameFontSize,
    usernameLineHeight: cfg.usernameLineHeight,
    messageFontSize: cfg.messageFontSize,
    messageLineHeight: cfg.messageLineHeight,
    messageFontWeight: cfg.messageFontWeight,
    usernameFontWeight: cfg.usernameFontWeight,
    imageScale: cfg.imageScale,
    messageColor: cfg.messageColor,
    blockedWords: cfg.blockedWords,
    fontFamily: cfg.fontFamily,
    youtubeSourceEnabled: cfg.youtubeSourceEnabled,
    youtubeChatChannels: cfg.youtubeChatChannels,
    chatDelayMs: 0,
    width: null,
    height: null,
    show7tvEmotes: cfg.show7tvEmotes,
    showBttvEmotes: cfg.showBttvEmotes,
    showFfzEmotes: cfg.showFfzEmotes,
    twitchChatChannels: cfg.twitchChatChannels,
    showYouTubeProfilePictures: cfg.showYouTubeProfilePictures
  },
  accountChatChannel: null
})

const sampleContextOf = (b: OverlayBootstrap, seed: number): SampleChatContext => {
  const configuredTwitch = Array.isArray(b.config.twitchChatChannels) ? b.config.twitchChatChannels : []
  const youtube = Array.isArray(b.config.youtubeChatChannels) ? b.config.youtubeChatChannels : []
  const youtubeChannelIds = youtube.length > 0 ? youtube : (b.config.youtubeSourceEnabled ? [''] : [])
  return {
    seed,
    twitchLogins: b.channels.length > 0 ? b.channels : configuredTwitch,
    youtubeChannelIds,
    blockedWords: (b.config.blockedWords ?? []).map((w) => w.toLowerCase())
  }
}

const TEXT_SHADOW_SOFT = '0 1px 2px rgba(0, 0, 0, 0.9), 0 0 3px rgba(0, 0, 0, 0.5)'
const TEXT_SHADOW_HARD =
  '-1px -1px 0 rgba(0, 0, 0, 0.9), 0 -1px 0 rgba(0, 0, 0, 0.9), ' +
  '1px -1px 0 rgba(0, 0, 0, 0.9), -1px 0 0 rgba(0, 0, 0, 0.9), ' +
  '1px 0 0 rgba(0, 0, 0, 0.9), -1px 1px 0 rgba(0, 0, 0, 0.9), ' +
  '0 1px 0 rgba(0, 0, 0, 0.9), 1px 1px 0 rgba(0, 0, 0, 0.9)'

const textShadowCss = (config: ChatOverlayConfig): string | undefined => {
  if (config.textShadow === 'OFF') return undefined
  if (config.textShadow === 'HARD') return TEXT_SHADOW_HARD
  if (config.textShadow === 'SOFT') return TEXT_SHADOW_SOFT
  return config.showTextShadow ? TEXT_SHADOW_SOFT : undefined
}

interface OverlayStyles {
  line: React.CSSProperties
  name: React.CSSProperties
  chip: React.CSSProperties
  avatar: React.CSSProperties
  authorAvatar: React.CSSProperties
  badge: React.CSSProperties
  emote: React.CSSProperties
}

const BADGE_BASE_PX = 18
const EMOTE_BASE_PX = 28

const buildStyles = (config: ChatOverlayConfig): OverlayStyles => {
  const badgeSize = Math.round(BADGE_BASE_PX * config.imageScale / 100)
  const emoteSize = Math.round(EMOTE_BASE_PX * config.imageScale / 100)
  return {
    line: {
      fontFamily: chatFontFamily(config.fontFamily),
      fontOpticalSizing: 'none',
      fontStyle: 'normal',
      fontWeight: effectiveChatFontWeight(
        config.fontFamily, config.messageFontWeight, CHAT_MESSAGE_WEIGHT_DEFAULT
      ),
      fontSize: `${config.messageFontSize}px`,
      lineHeight: `${config.messageLineHeight}px`,
      color: config.messageColor,
      textShadow: textShadowCss(config),
      overflowWrap: 'anywhere',
      padding: '4px 16px'
    },
    name: {
      fontWeight: effectiveChatFontWeight(
        config.fontFamily, config.usernameFontWeight, CHAT_USERNAME_WEIGHT_DEFAULT
      ),
      fontSize: `${config.usernameFontSize}px`,
      lineHeight: `${config.usernameLineHeight}px`
    },
    chip: {
      display: 'inline-block',
      fontSize: '11px',
      lineHeight: '16px',
      fontWeight: preRangePinnedWeight(config.fontFamily, 600),
      padding: '0 5px',
      marginRight: '6px',
      borderRadius: '4px',
      background: 'rgba(0, 0, 0, 0.55)',
      verticalAlign: '2px',
      textShadow: 'none'
    },
    avatar: {
      display: 'inline-block',
      width: `${badgeSize}px`,
      height: `${badgeSize}px`,
      marginRight: '6px',
      borderRadius: '4px',
      verticalAlign: 'middle'
    },
    authorAvatar: {
      display: 'inline-block',
      width: `${badgeSize}px`,
      height: `${badgeSize}px`,
      marginRight: '6px',
      borderRadius: '50%',
      verticalAlign: 'middle'
    },
    badge: {
      display: 'inline-block',
      width: `${badgeSize}px`,
      height: `${badgeSize}px`,
      marginRight: '4px',
      verticalAlign: 'middle'
    },
    emote: {
      display: 'inline-block',
      height: `${emoteSize}px`,
      margin: '-3px 0',
      verticalAlign: 'middle'
    }
  }
}

const containerStyle = (fillHost: boolean): React.CSSProperties => ({
  height: fillHost ? '100%' : '100vh',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  overflow: 'hidden',
  padding: '8px 0'
})

const SegmentSpan = ({
  segment,
  emoteStyle
}: {
  segment: OverlaySegment
  emoteStyle: React.CSSProperties
}): JSX.Element => {
  if (segment.kind === 'emote') {
    return <img src={nativeEmoteUrlLarge(segment.id)} alt={segment.alt} title={segment.alt} style={emoteStyle} />
  }
  if (segment.kind === 'emote3p') {
    return <img src={segment.entry.urls.x4 ?? segment.entry.urls.x2} alt={segment.alt} title={segment.alt} style={emoteStyle} />
  }
  return <>{segment.text}</>
}

interface SegmentUnit {
  base: OverlaySegment
  overlays: ThirdPartyEmoteSegment[]
}

const isEmoteBase = (segment: OverlaySegment): boolean =>
  segment.kind === 'emote' || segment.kind === 'emote3p'

const isWhitespaceText = (segment: OverlaySegment): boolean =>
  segment.kind === 'text' && segment.text.trim() === ''

const pairZeroWidth = (segments: OverlaySegment[]): SegmentUnit[] =>
  segments.reduce<SegmentUnit[]>((units, segment) => {
    if (segment.kind === 'emote3p' && segment.entry.zeroWidth) {
      const prev = units[units.length - 1]
      if (prev !== undefined && isEmoteBase(prev.base)) {
        return [...units.slice(0, -1), { base: prev.base, overlays: [...prev.overlays, segment] }]
      }
      const beforeSpace = units[units.length - 2]
      if (
        prev !== undefined && isWhitespaceText(prev.base) && prev.overlays.length === 0 &&
        beforeSpace !== undefined && isEmoteBase(beforeSpace.base)
      ) {
        return [
          ...units.slice(0, -2),
          { base: beforeSpace.base, overlays: [...beforeSpace.overlays, segment] }
        ]
      }
    }
    return [...units, { base: segment, overlays: [] }]
  }, [])

const UnitSpan = ({
  unit,
  emoteStyle
}: {
  unit: SegmentUnit
  emoteStyle: React.CSSProperties
}): JSX.Element => {
  if (unit.overlays.length === 0) {
    return <SegmentSpan segment={unit.base} emoteStyle={emoteStyle} />
  }
  return (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      <SegmentSpan segment={unit.base} emoteStyle={emoteStyle} />
      {unit.overlays.map((overlay, i) => (
        <img
          key={i}
          src={overlay.entry.urls.x4 ?? overlay.entry.urls.x2}
          alt={overlay.alt}
          title={overlay.alt}
          style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', top: 0, height: '100%' }}
        />
      ))}
    </span>
  )
}

const ChannelPill = ({
  pill,
  styles
}: {
  pill: ChannelPillData
  styles: OverlayStyles
}): JSX.Element => {
  if (pill.imageUrl !== null) {
    const handlePictureError = pill.onImageError
    if (handlePictureError !== undefined) {
      return (
        <img
          src={pill.imageUrl}
          alt={pill.label}
          title={pill.label}
          referrerPolicy='no-referrer'
          onError={handlePictureError}
          style={styles.avatar}
        />
      )
    }
    return <img src={pill.imageUrl} alt={pill.label} title={pill.label} style={styles.avatar} />
  }
  return (
    <span style={{ ...styles.chip, color: fallbackColor(pill.label) }}>
      {pill.label}
    </span>
  )
}

const hideBrokenImage = (e: React.SyntheticEvent<HTMLImageElement>): void => {
  e.currentTarget.style.display = 'none'
}

const ChatLine = ({
  message,
  styles,
  badgeUrlFor,
  pillFor,
  showChannel,
  showYouTubeRoles,
  showAuthorAvatar,
  showPlatformMark,
  lookupFor
}: {
  message: ChatMessage
  styles: OverlayStyles
  badgeUrlFor: (roomId: string, badge: ChatBadge) => string | null
  pillFor: (message: ChatMessage) => ChannelPillData | null
  showChannel: boolean
  showYouTubeRoles: boolean
  showAuthorAvatar: boolean
  showPlatformMark: boolean
  lookupFor: (name: string, roomId: string) => EmoteEntry | null
}): JSX.Element => {
  const badgeUrls = message.badges
    .map((b) => badgeUrlFor(message.roomId, b))
    .filter((url): url is string => url !== null)
  const units = pairZeroWidth(applyThirdPartyEmotes(
    messageSegments(message.text, message.emotes),
    (name) => lookupFor(name, message.roomId)
  ))
  const textStyle = message.isAction ? { color: message.color } : undefined
  const pill = showChannel ? pillFor(message) : null
  const isYouTube = message.platform === 'YOUTUBE'
  return (
    <div style={styles.line}>
      {pill !== null && (
        <ChannelPill pill={pill} styles={styles} />
      )}
      {badgeUrls.map((url, i) => (
        <img key={i} src={url} alt='' style={styles.badge} />
      ))}
      {isYouTube && showYouTubeRoles && (
        <YouTubeRoleBadges roles={message.youtubeRoles} style={styles.badge} />
      )}

      {showPlatformMark && (
        <img
          src={PLATFORM_MARKS[message.platform].src}
          alt={PLATFORM_MARKS[message.platform].label}
          title={PLATFORM_MARKS[message.platform].label}
          style={PLATFORM_MARK_STYLE}
        />
      )}
      {isYouTube && showAuthorAvatar && message.authorAvatarUrl !== undefined && (
        <img
          src={message.authorAvatarUrl}
          alt=''
          referrerPolicy='no-referrer'
          onError={hideBrokenImage}
          style={styles.authorAvatar}
        />
      )}
      <span style={{ ...styles.name, color: message.color }}>{message.displayName}</span>
      <span style={textStyle}>
        {message.isAction ? ' ' : ': '}
        {units.map((unit, i) => (
          <UnitSpan key={i} unit={unit} emoteStyle={styles.emote} />
        ))}
      </span>
    </div>
  )
}

export const ChatOverlayView = ({
  slug,
  chatId,
  token,
  fillHost = false,
  standalone = false,
  preview = null,
  previewConfig = null
}: {
  slug?: string
  chatId: string
  token: string
  fillHost?: boolean
  standalone?: boolean
  preview?: OverlayPreviewSeam | null
  previewConfig?: ChatOverlayPreviewConfig | null
}): JSX.Element => {
  const [searchParams] = useSearchParams()
  const delayParam = searchParams.get('delay')
  const urlDelayMs = useMemo(() => {
    if (delayParam === null) return 0
    const seconds = Number(delayParam)
    if (!Number.isFinite(seconds) || seconds <= 0) return 0
    return Math.min(seconds, MAX_DELAY_SECONDS) * 1000
  }, [delayParam])

  const [bootstrap, setBootstrap] = useState<OverlayBootstrap | null>(null)
  const previewOn = previewSeamActive(preview)
  const mockup = previewOn && preview.mode === 'mockup'
  const feed: ChatPreviewFeed | null = previewOn ? preview.chatFeed : null
  const feedKey = chatId === '' ? 'mockup' : chatId
  const feedRef = useRef<ChatPreviewFeed | null>(null)
  feedRef.current = feed
  const feedKeyRef = useRef(feedKey)
  feedKeyRef.current = feedKey
  const delayMs = Math.max(
    urlDelayMs,
    bootstrap === null ? 0 : chatDelayMsOf(bootstrap.config)
  )
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const pendingRef = useRef<PendingMessage[]>([])
  const [emoteLookup, setEmoteLookup] = useState<EmoteLookup | null>(null)
  const emoteStoreRef = useRef<EmoteStore | null>(null)
  const [marksActive, setMarksActive] = useState(false)
  const [ytChannels, setYtChannels] = useState<Record<string, YouTubeSourceMeta>>({})
  const [badYtPictures, setBadYtPictures] = useState<ReadonlySet<string>>(() => new Set())

  const bootstrapRef = useRef<OverlayBootstrap | null>(null)
  bootstrapRef.current = bootstrap

  const clearView = useCallback((reason: string): void => {
    log({ stage: 'config-deleted', chatId, reason })
    pendingRef.current = []
    setMessages([])
    if (feedRef.current != null) feedRef.current.removeReal(feedKeyRef.current, () => true)
    setBootstrap(null)
  }, [chatId])
  const clearViewRef = useRef(clearView)
  clearViewRef.current = clearView

  const refetchRef = useRef<() => void>(() => {})

  const lastConfigVersionRef = useRef(0)

  useEffect(() => {
    const missingParams = slug === '' || chatId === '' || token === ''
    if (missingParams) return
    if (mockup) return
    let cancelled = false
    let retryTimer: ReturnType<typeof globalThis.setTimeout> | undefined
    const load = (): void => {
      const versionAtStart = lastConfigVersionRef.current
      graphqlRequest<{ chatOverlay: ChatOverlayData | null }>(
        getChatOverlayDataQuery,
        slug === undefined ? { chatId } : { slug, chatId },
        token
      )
        .then((res) => {
          if (cancelled) return
          if (lastConfigVersionRef.current !== versionAtStart) {
            load()
            return
          }
          if (res.chatOverlay === null) {
            if (bootstrapRef.current !== null) clearViewRef.current('refetch-null')
            return
          }
          const folded = foldBootstrap(res.chatOverlay)
          setBootstrap(folded)
          if (shouldRetryEmptyChannels(folded)) {
            retryTimer = globalThis.setTimeout(load, EMPTY_CHANNELS_RETRY_MS)
          }
        })
        .catch((err: unknown) => {
          log({ bootstrap: 'failed', err: err instanceof Error ? err.name : 'unknown' })
          if (!cancelled) retryTimer = globalThis.setTimeout(load, BOOTSTRAP_RETRY_MS)
        })
    }
    refetchRef.current = load
    load()
    return () => {
      cancelled = true
      refetchRef.current = () => {}
      globalThis.clearTimeout(retryTimer)
    }
  }, [slug, chatId, token, mockup])

  const broadcasterIdsKey = bootstrap === null ? null : bootstrap.broadcasterUserIds.join(' ')
  const show7tv = bootstrap?.config.show7tvEmotes !== false
  const showBttv = bootstrap?.config.showBttvEmotes !== false
  const showFfz = bootstrap?.config.showFfzEmotes !== false
  const lastProvidersKeyRef = useRef<string | null>(null)
  useEffect(() => {
    if (broadcasterIdsKey === null) return
    const ids = broadcasterIdsKey === '' ? [] : broadcasterIdsKey.split(' ')
    const providersKey = `${String(show7tv)} ${String(showBttv)} ${String(showFfz)}`
    const freshCheck = lastProvidersKeyRef.current !== null && lastProvidersKeyRef.current !== providersKey
    lastProvidersKeyRef.current = providersKey
    const store = createEmoteStore(
      ids,
      (lookup) => setEmoteLookup(() => lookup),
      { providers: { sevenTv: show7tv, bttv: showBttv, ffz: showFfz }, freshCheck }
    )
    emoteStoreRef.current = store
    return () => {
      emoteStoreRef.current = null
      store.dispose()
    }
  }, [broadcasterIdsKey, show7tv, showBttv, showFfz])

  const blockedWords = useMemo(() => {
    if (bootstrap === null) return []
    return (bootstrap.config.blockedWords ?? []).map((w) => w.toLowerCase())
  }, [bootstrap])

  const blockedWordsRef = useRef<string[]>([])
  blockedWordsRef.current = blockedWords

  const ingestMessage = useCallback((message: ChatMessage): void => {
    if (blockedWords.length > 0) {
      const text = message.text.toLowerCase()
      if (blockedWords.some((w) => text.includes(w))) return
    }
    if (message.platform === 'YOUTUBE') setMarksActive(true)
    const tail = pendingRef.current[pendingRef.current.length - 1]
    const paceUntil = tail === undefined
      ? message.receivedAt
      : Math.max(
        message.receivedAt,
        tail.paceUntil + Math.min(
          Math.max(message.publishedAt - tail.message.publishedAt, 0),
          PACE_GAP_CAP_MS
        )
      )
    const next = [...pendingRef.current, { message, paceUntil }]
    pendingRef.current = next.length > MAX_PENDING_MESSAGES
      ? next.slice(-MAX_PENDING_MESSAGES)
      : next
  }, [blockedWords])

  const ingestRef = useRef(ingestMessage)
  ingestRef.current = ingestMessage

  const channelsKey = bootstrap === null ? null : bootstrap.channels.join(' ')
  useEffect(() => {
    if (channelsKey === null || channelsKey === '') return
    const handlers = {
      onMessage: (message: ChatMessage) => ingestRef.current(message),
      onMessageDeleted: ({ targetMsgId }: { channel: string, targetMsgId: string }) => {
        pendingRef.current = pendingRef.current.filter((e) => e.message.id !== targetMsgId)
        setMessages((prev) => prev.filter((m) => m.id !== targetMsgId))
        if (feedRef.current != null) feedRef.current.removeReal(feedKeyRef.current, (m) => m.id === targetMsgId)
      },
      onChatCleared: ({ channel, targetLogin }: { channel: string, targetLogin: string | null }) => {
        const hit = (m: ChatMessage): boolean =>
          m.channel === channel && (targetLogin === null || m.login === targetLogin)
        pendingRef.current = pendingRef.current.filter((e) => !hit(e.message))
        setMessages((prev) => prev.filter((m) => !hit(m)))
        if (feedRef.current != null) feedRef.current.removeReal(feedKeyRef.current, hit)
      },
      onConnected: () => emoteStoreRef.current?.refreshNow()
    }
    const connection = connectTwitchChat(channelsKey.split(' '), handlers)
    return connection.close
  }, [channelsKey])

  const onConfigEnvelope = useCallback((envelope: AccountChatEnvelope): void => {
    if (envelope.chatId !== chatId) return
    const version = envelope.version
    if (typeof version !== 'number' || version <= lastConfigVersionRef.current) return
    lastConfigVersionRef.current = version
    if (envelope.deleted === true) {
      clearView('deletion-envelope')
      return
    }
    if (envelope.data == null) return
    const folded = foldBootstrap(envelope.data)
    setBootstrap(folded)
    if (shouldRetryEmptyChannels(folded)) refetchRef.current()
  }, [chatId, clearView])
  const onConfigEnvelopeRef = useRef(onConfigEnvelope)
  onConfigEnvelopeRef.current = onConfigEnvelope

  const ytAcceptRef = useRef<(channelId: string | undefined) => boolean>(() => false)
  const ytConfig = bootstrap === null ? null : bootstrap.config
  ytAcceptRef.current = (channelId: string | undefined): boolean => {
    if (ytConfig === null) return false
    if (typeof channelId === 'string' && channelId !== '') {
      const list = Array.isArray(ytConfig.youtubeChatChannels)
        ? ytConfig.youtubeChatChannels
        : []
      return list.includes(channelId)
    }
    return ytConfig.youtubeSourceEnabled
  }

  const accountChatChannel = bootstrap === null ? null : bootstrap.accountChatChannel
  useEffect(() => {
    if (accountChatChannel === null || accountChatChannel === '' || token === '') return
    const seenIds = new Set<string>()
    const rememberSeen = (id: string): void => {
      seenIds.add(id)
      if (seenIds.size > 4000) {
        const keep = Array.from(seenIds).slice(-2000)
        seenIds.clear()
        keep.forEach((k) => seenIds.add(k))
      }
    }
    const client = buildAppSyncWsClient(token)
    const unsubscribe = client.subscribe(
      { query: WEBSOCKET_SUB_QUERY, variables: { channel: accountChatChannel } },
      {
        next: (data: any) => {
          const dataField = data?.WebsocketSub?.data
          if (dataField == null) return
          let envelope: AccountChatEnvelope
          try {
            envelope = typeof dataField === 'string' ? JSON.parse(dataField) : dataField
          } catch (err) {
            log({ ytLane: 'envelope parse failed', err: err instanceof Error ? err.name : 'unknown' })
            return
          }
          if (envelope.kind === 'CHAT_OVERLAY_CONFIG') {
            onConfigEnvelopeRef.current(envelope)
            return
          }
          if (envelope.kind === 'YT_BAN') {
            const authorChannelId = envelope.authorChannelId
            if (typeof authorChannelId !== 'string' || authorChannelId === '') return
            const hit = (m: ChatMessage): boolean =>
              m.platform === 'YOUTUBE' && m.login === authorChannelId
            pendingRef.current = pendingRef.current.filter((e) => !hit(e.message))
            setMessages((prev) => prev.filter((m) => !hit(m)))
            if (feedRef.current != null) feedRef.current.removeReal(feedKeyRef.current, hit)
            return
          }
          if (envelope.kind !== 'YT_CHAT' || !Array.isArray(envelope.messages)) return
          const sourceChannelId = typeof envelope.channelId === 'string' && envelope.channelId !== ''
            ? envelope.channelId
            : undefined
          if (!ytAcceptRef.current(sourceChannelId)) return
          if (sourceChannelId !== undefined) {
            const stampTitle = typeof envelope.channelTitle === 'string' && envelope.channelTitle !== ''
              ? envelope.channelTitle
              : null
            const stampImage = httpsUrlOrUndefined(envelope.channelProfileImageUrl) ?? null
            if (stampTitle !== null || stampImage !== null) {
              setYtChannels((prev) => {
                const cur = prev[sourceChannelId]
                const title = stampTitle ?? cur?.title ?? null
                const imageUrl = stampImage ?? cur?.imageUrl ?? null
                if (cur !== undefined && cur.title === title && cur.imageUrl === imageUrl) return prev
                return { ...prev, [sourceChannelId]: { title, imageUrl } }
              })
              if (feedRef.current != null) feedRef.current.noteYouTubeChannel(feedKeyRef.current, sourceChannelId, { title: stampTitle, imageUrl: stampImage })
            }
          }
          for (const raw of envelope.messages) {
            const message = youtubeChatMessage(raw, sourceChannelId)
            if (message === null || seenIds.has(message.id)) continue
            rememberSeen(message.id)
            ingestRef.current(message)
          }
        },
        error: (err: unknown) => {
          log({ ytLane: 'subscription error', err: err instanceof Error ? err.name : 'unknown' })
        },
        complete: () => { }
      }
    )
    const offConnected = client.on('connected', () => refetchRef.current())
    return () => {
      offConnected()
      unsubscribe()
      client.dispose()
    }
  }, [accountChatChannel, token])

  useEffect(() => {
    if (mockup) return
    const interval = globalThis.setInterval(() => {
      const pending = pendingRef.current
      if (pending.length === 0) return
      const now = Date.now()
      const newestPublishedAt = pending.reduce(
        (max, e) => Math.max(max, e.message.publishedAt), 0
      )
      const releasable = (e: PendingMessage): boolean => {
        if (now - e.message.receivedAt < delayMs) return false
        return now >= e.paceUntil ||
          newestPublishedAt - e.message.publishedAt >= REPLAY_CAP_MS
      }
      const due = pending.filter(releasable)
      if (due.length === 0) return
      const remaining = pending.filter((e) => !releasable(e))
      const released = due.sort((a, b) => a.message.publishedAt - b.message.publishedAt)
      const blockedNow = blockedWordsRef.current
      const batch = blockedNow.length === 0
        ? released
        : released.filter((e) => {
          const text = e.message.text.toLowerCase()
          return !blockedNow.some((w) => text.includes(w))
        })
      const lastReleasedPublishedAt = released[released.length - 1].message.publishedAt
      pendingRef.current = remaining.reduce<PendingMessage[]>((acc, e) => {
        const prev = acc[acc.length - 1]
        const fromSlot = prev === undefined ? now : prev.paceUntil
        const fromPublishedAt = prev === undefined ? lastReleasedPublishedAt : prev.message.publishedAt
        const gap = Math.min(
          Math.max(e.message.publishedAt - fromPublishedAt, 0),
          PACE_GAP_CAP_MS
        )
        return [...acc, { ...e, paceUntil: Math.min(e.paceUntil, fromSlot + gap) }]
      }, [])
      if (batch.length > 0) {
        setMessages((prev) => [...prev, ...batch.map((e) => e.message)].slice(-MAX_MESSAGES))
        if (feedRef.current != null) feedRef.current.appendReal(feedKeyRef.current, batch.map((e) => e.message))
      }
    }, FLUSH_INTERVAL_MS)
    return () => globalThis.clearInterval(interval)
  }, [delayMs, mockup])

  const fallbackBootstrap = useMemo(
    () => (previewOn && previewConfig != null ? previewBootstrapOf(previewConfig) : null),
    [previewOn, previewConfig]
  )
  const renderBootstrap = bootstrap ?? fallbackBootstrap
  useEffect(() => {
    if (feed == null) return
    return feed.hold(feedKey)
  }, [feed, feedKey])
  const feedSnapshot = useChatFeedSnapshot(feed, feedKey)
  const localRealSeenRef = useRef(false)
  if (previewOn && messages.length > 0) localRealSeenRef.current = true
  const realSeen = feedSnapshot != null ? feedSnapshot.realSeen : localRealSeenRef.current
  const reducedMotion = usePrefersReducedMotion(previewOn)
  const tabVisible = useDocumentVisible(previewOn)
  const sampleSeed = previewOn ? elementSeed(preview.seed, chatId) : 0
  const sampleWindow = useSampleChatWindow({
    enabled: previewOn && !realSeen,
    seed: sampleSeed,
    reducedMotion,
    visible: tabVisible
  })
  const sampleMessages = useMemo((): ChatMessage[] => {
    if (!previewOn || realSeen || renderBootstrap == null) return []
    const ctx = sampleContextOf(renderBootstrap, sampleSeed)
    return sampleWindow
      .map((n) => sampleChatMessage(n, ctx))
      .filter((m): m is ChatMessage => m != null)
  }, [previewOn, realSeen, renderBootstrap, sampleSeed, sampleWindow])
  const pillYtChannels = useMemo(
    () => (feedSnapshot == null ? ytChannels : { ...feedSnapshot.ytChannels, ...ytChannels }),
    [feedSnapshot, ytChannels]
  )

  const badgeUrlFor = useCallback((roomId: string, badge: ChatBadge): string | null => {
    if (renderBootstrap == null || !renderBootstrap.config.showBadges) return null
    const channelUrl = renderBootstrap.channelBadges[roomId]?.[badge.set]?.[badge.version]
    if (channelUrl != null) return channelUrl
    return renderBootstrap.globalBadges[badge.set]?.[badge.version] ?? null
  }, [renderBootstrap])

  const sampleBadgeUrlFor = useCallback((_roomId: string, badge: ChatBadge): string | null => {
    if (renderBootstrap == null || !renderBootstrap.config.showBadges) return null
    const fromBootstrap = renderBootstrap.globalBadges[badge.set]?.[badge.version]
    if (fromBootstrap != null) return fromBootstrap
    return SAMPLE_BADGE_ART[`${badge.set}/${badge.version}`] ?? null
  }, [renderBootstrap])

  const pillFor = useCallback((message: ChatMessage): ChannelPillData | null => {
    if (message.platform !== 'YOUTUBE') {
      return {
        label: message.channel,
        imageUrl: renderBootstrap == null ? null : (renderBootstrap.avatars[message.channel] ?? null)
      }
    }
    const source = message.sourceChannelId
    if (source === undefined) return null
    const meta = pillYtChannels[source]
    if (meta === undefined) return null
    const picture = meta.imageUrl
    if (picture !== null && !badYtPictures.has(picture)) {
      return {
        label: meta.title ?? source,
        imageUrl: picture,
        onImageError: () => {
          setBadYtPictures((prev) => (prev.has(picture) ? prev : new Set([...prev, picture])))
        }
      }
    }
    if (meta.title !== null) return { label: meta.title, imageUrl: null }
    return null
  }, [renderBootstrap, pillYtChannels, badYtPictures])

  const samplePillFor = useCallback((message: ChatMessage): ChannelPillData | null => {
    const pill = pillFor(message)
    return pill != null && pill.label === '' ? null : pill
  }, [pillFor])

  const lookupFor = useCallback((name: string, roomId: string): EmoteEntry | null =>
    emoteLookup === null ? null : emoteLookup(name, roomId), [emoteLookup])

  const configFontKey = renderBootstrap == null ? null : renderBootstrap.config.fontFamily
  const fontReady = useFontGate(configFontKey)
  const [paintedFontKey, setPaintedFontKey] = useState<string | null>(null)
  useEffect(() => {
    if (configFontKey !== null && fontReady) setPaintedFontKey(configFontKey)
  }, [configFontKey, fontReady])

  const styles = useMemo(() => {
    if (renderBootstrap == null || paintedFontKey == null) return null
    return buildStyles({ ...renderBootstrap.config, fontFamily: paintedFontKey })
  }, [renderBootstrap, paintedFontKey])

  const fitParam = searchParams.get('fit')
  const storedDims = standalone && fitParam !== 'off' && bootstrap !== null
    ? storedDimensionsOf(bootstrap.config)
    : null
  const designMode: OverlayFitMode = storedDims === null
    ? { kind: 'off' }
    : { kind: 'design', width: storedDims.width, height: storedDims.height }
  const designBox = useOverlayFit(designMode)

  const column = (host: boolean): JSX.Element => {
    if (renderBootstrap == null || styles == null) {
      return <div style={containerStyle(host)} />
    }
    const sourceCount = renderBootstrap.channels.length + youtubeSourceCountOf(renderBootstrap.config)
    const showChannel = renderBootstrap.config.showChannelPills && sourceCount > 1
    const showYouTubeRoles = renderBootstrap.config.showBadges
    const showAuthorAvatar = renderBootstrap.config.showYouTubeProfilePictures !== false
    if (previewOn) {
      if (realSeen) {
        const realRows = feedSnapshot != null ? feedSnapshot.real : messages
        const realMarks = marksActive || feedSnapshot?.youtubeSeen === true
        return (
          <div style={{ ...containerStyle(host), position: 'relative' }}>
            {realRows.map((message) => (
              <ChatLine
                key={message.id}
                message={message}
                styles={styles}
                badgeUrlFor={badgeUrlFor}
                pillFor={pillFor}
                showChannel={showChannel}
                showYouTubeRoles={showYouTubeRoles}
                showAuthorAvatar={showAuthorAvatar}
                showPlatformMark={realMarks}
                lookupFor={lookupFor}
              />
            ))}
          </div>
        )
      }
      const sampleMarks = sampleMessages.some((m) => m.platform === 'YOUTUBE')
      return (
        <div style={{ ...containerStyle(host), position: 'relative' }}>
          <div data-testid='overlay-sample-rows' style={{ opacity: SAMPLE_OPACITY }}>
            {sampleMessages.map((message) => (
              <ChatLine
                key={message.id}
                message={message}
                styles={styles}
                badgeUrlFor={sampleBadgeUrlFor}
                pillFor={samplePillFor}
                showChannel={showChannel}
                showYouTubeRoles={showYouTubeRoles}
                showAuthorAvatar={showAuthorAvatar}
                showPlatformMark={sampleMarks}
                lookupFor={lookupFor}
              />
            ))}
          </div>
          <span data-testid='overlay-sample-tag' style={sampleTagStyle(renderBootstrap.config.messageFontSize)}>Sample</span>
        </div>
      )
    }
    return (
      <div style={containerStyle(host)}>
        {messages.map((message) => (
          <ChatLine
            key={message.id}
            message={message}
            styles={styles}
            badgeUrlFor={badgeUrlFor}
            pillFor={pillFor}
            showChannel={showChannel}
            showYouTubeRoles={showYouTubeRoles}
            showAuthorAvatar={showAuthorAvatar}
            showPlatformMark={marksActive}
            lookupFor={lookupFor}
          />
        ))}
      </div>
    )
  }

  if (!standalone) return column(fillHost)

  if (storedDims !== null) {
    const boxStyle: React.CSSProperties = {
      width: storedDims.width,
      height: storedDims.height,
      overflow: 'hidden'
    }
    if (designBox.scale !== 1) {
      boxStyle.transform = `scale(${designBox.scale})`
      boxStyle.transformOrigin = 'top left'
    }
    return (
      <div data-testid='chat-dimensions-box' style={boxStyle}>
        {column(true)}
      </div>
    )
  }

  return (
    <OverlayAutoFit mode={{ kind: 'factor' }}>
      {(supersampled: boolean) => column(supersampled)}
    </OverlayAutoFit>
  )
}
